import React, { useContext, useEffect, useRef, useState } from 'react'
import { HEADING_TYPE, DIVIDER_SIZE, BUTTON_TYPE } from '@helpers/constants'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Button from '@components/Button'
import { nanoid } from 'nanoid'
import { renderToStaticMarkup } from 'react-dom/server'
import { connectToEditorjs } from '@blocks/connectToEditorjs'
import Icon from '@components/Icon'
import Form from '@components/Form'
import EditExerciseWrapper from '@components/EditExerciseWrapper'
import { uploadImage } from '@helpers/api'
import { storagePath } from '@helpers/variables'
import { addOnSaveCallback } from '@state/actions/editorActions'
import { AppContext } from '@state/context'
import { getImageSrc } from '@helpers/utilities'

const WordExplanationComponent = (props) => {
  const { initialState, handleStateChange, onSave, id } = props
  const [state, setState] = useState(initialState)
  const [, dispatch] = useContext(AppContext)

  const { name = '', type = 'word-explanation', preamble = {}, words = [] } = state

  /**
   * Broadcast state change up
   */
  useEffect(() => {
    handleStateChange(state)
  }, [state])

  useEffect(() => {
    dispatch(
      addOnSaveCallback({
        id,
        callback: async () => {
          const newWords = await Promise.all(
            words?.map(async (word) => {
              if (!(word?.image?.previewUrl instanceof File)) return word

              try {
                const filename = await uploadImage(word.image.previewUrl)

                return {
                  ...word,
                  image: { filename: `${storagePath}/${filename}` },
                }
              } catch (error) {
                console.log(error)

                return word
              }
            })
          )

          handleStateChange({
            ...state,
            words: newWords,
          })
        },
      })
    )
  }, [state])

  /**
   * Remove question
   * @param {*} word
   */
  const removeWord = (word) => {
    setState({
      ...state,
      words: words.filter((q, k) => k !== word),
    })
  }

  const moveWord = (fromIndex, toIndex) => {
    const element = words[fromIndex]
    words.splice(fromIndex, 1)
    words.splice(toIndex, 0, element)

    setState({
      ...state,
      words: [...words],
    })
  }

  /**
   * Add new word
   */
  const addNewWord = () => {
    setState({
      ...state,
      words: [
        ...words,
        {
          id: nanoid(),
          word: '',
          explanation: {},
          image: {
            previewUrl: null,
          },
        },
      ],
    })
  }

  /**
   * @param {*} wordKey
   * @param {*} attribute
   * @param {*} value
   */
  const updateWords = (wordKey, attribute, value = false) => {
    const newWords = words.map((word, wKey) => {
      if (wordKey === wKey) {
        if (attribute === 'image') {
          word[attribute] = { previewUrl: value }
        } else {
          word[attribute] = value
        }
      }
      return word
    })
    setState({
      ...state,
      words: newWords,
    })
  }

  return (
    <div>
      <EditExerciseWrapper type={'Ordförklaring'} onSave={onSave}>
        <Heading headingLevel={HEADING_TYPE.H2}>Välj övningstyp</Heading>
        <Button
          marginRight
          type={
            type === 'word-explanation'
              ? BUTTON_TYPE.PRIMARY
              : BUTTON_TYPE.SECONDARY
          }
          handleClick={() => {
            setState({
              ...state,
              type: 'word-explanation',
            })
          }}
        >
          Ordförklaring
        </Button>
        <Button
          handleClick={() => {
            setState({
              ...state,
              type: 'toolbox',
            })
          }}
          type={
            type === 'toolbox' ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY
          }
        >
          Verktygslåda
        </Button>
        <Divider size={DIVIDER_SIZE.SMALL} />

        <Heading headingLevel={HEADING_TYPE.H3}>Namn på övningen</Heading>
        <Form.Editor
          content={name}
          handleChange={(value) => setState({ ...state, name: value })}
          elementId={`word-explanation-name-${id}`}
        />
        <Divider size={DIVIDER_SIZE.SMALL} />
        <Heading headingLevel={HEADING_TYPE.H3}>Inledande text</Heading>
        <Form.Editor
          content={preamble}
          handleChange={(value) => setState({ ...state, preamble: value })}
          // placeholder={'Skriv en inledande text om övningen'}
          elementId={`word-explanation-preamble-${id}`}
        />
        <Divider size={DIVIDER_SIZE.SMALL} />
        {/* ------Words------ */}
        {words &&
          words.map((word, wordKey) => {
            if (!word) return

            if (!word?.id) {
              updateWords(wordKey, 'id', nanoid())
            }

            return (
              <li key={word?.id} className={'list-none'}>
                <Heading headingLevel={HEADING_TYPE.H3}>
                  Ord {wordKey + 1}
                </Heading>
                <Divider size={DIVIDER_SIZE.LINE} line />
                <Divider size={DIVIDER_SIZE.SMALL} />

                <div className={'flex'}>
                  <div className={'flex-shrink-0'}>
                    <Form.ImageInput
                      handleImageChange={(e) => {
                        e.preventDefault()

                        updateWords(wordKey, 'image', e.target.files[0])
                      }}
                      id={wordKey}
                      imagePreviewUrl={getImageSrc(word?.image)}
                      inputKey={wordKey}
                      name={'image'}
                      onDelete={() => {
                        updateWords(wordKey, 'image', null)
                      }}
                    />
                  </div>
                  <div className={'w-full ml-4'}>
                    <p>Fyll i ordet som passar till bilden</p>
                    <Form.Input
                      placeholder='Skriv ditt ord här'
                      defaultValue={word.word}
                      onChange={(e) =>
                        updateWords(wordKey, 'word', e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className={'mt-5 mb-3'}>
                  <Form.Editor
                    content={word.explanation}
                    handleChange={(value) => updateWords(wordKey, 'explanation', value)}
                    // placeholder='Skriv en kort förklaring här'
                    elementId={`word-explanation-editor-${word.id}`}
                  />
                </div>
                <Button
                  marginRight
                  type={BUTTON_TYPE.PRIMARY}
                  className={'mb-[10px]'}
                  handleClick={() => removeWord(wordKey)}
                >
                  Ta bort
                </Button>
                <Button
                  marginRight
                  disabled={wordKey === 0}
                  type={BUTTON_TYPE.PRIMARY}
                  handleClick={() => moveWord(wordKey, wordKey - 1)}
                >
                  Flytta upp
                </Button>
                <Button
                  disabled={wordKey + 1 === words.length}
                  type={BUTTON_TYPE.PRIMARY}
                  handleClick={() => moveWord(wordKey, wordKey + 1)}
                >
                  Flytta ner
                </Button>

                <Divider
                  size={
                    wordKey + 1 === words.length
                      ? DIVIDER_SIZE.MEDIUM
                      : DIVIDER_SIZE.SMALL
                  }
                />
              </li>
            )
          })}
        <Button type={BUTTON_TYPE.PRIMARY} handleClick={addNewWord}>
          Lägg till nytt ord
        </Button>

        <Divider size={DIVIDER_SIZE.MEDIUM} />
      </EditExerciseWrapper>
    </div>
  )
}

export const WordExplanation = connectToEditorjs(
  WordExplanationComponent,
  'Ordförklaring',
  renderToStaticMarkup(<Icon icon={'Download'} />)
)
